import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AccountService } from "../../../services/account.service";
import { filter, map, Observable, take } from "rxjs";
import {
  ApiRequestError,
  CognitoAuthenticatedUser,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  UserDto
} from "../../../models";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import {
  AccountAttributeChangeModalComponent
} from "../../../components/account-attribute-change-modal/account-attribute-change-modal.component";
import { TranslateService } from "@ngx-translate/core";
import { ApiHttpService } from "../../../services/api-http.service";
import { DataStorageService } from "../../../services/data-storage.service";
import { Router } from "@angular/router";
import {
  DEFAULT_COUNTRY_CODE,
  countryNameKey,
  generateDaysForMonthToDisplay,
  generateMonthsToDisplay,
  generateYearsToDisplay,
  hashFields,
  normalizeEmail
} from "../../../utils";
import { DialogService } from "../../../services/dialog.service";
import { ImageCropperService } from "../../../components/image-cropper-modal/image-cropper.service";

/**
 * Labels for the fields CloudInsurance does not accept from the portal. They
 * identify the policyholder, so `/save-user` mails the change to customer
 * service, answers with the old value and names the fields in
 * `pendingSupportFields`. A name without a label here is left out.
 */
const SUPPORT_FIELD_LABEL_KEYS: Record<string, string> = {
  firstName: 'account.first-name-title',
  phoneNumber: 'account.phone-title',
  parent_birthdate: 'account.date-of-birth',
  country: 'account.country-title',
};

@Component({
  selector: 'cpf-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountComponent implements OnInit, OnDestroy {
  @Input() showBackButton = true;

  @Output()
  backToProfile: EventEmitter<void> = new EventEmitter<void>();

  cognitoUser$: Observable<CognitoAuthenticatedUser | null>;
  user?: UserDto;
  initialCognitoUserData?: CognitoAuthenticatedUser & { avatarImgFilename?: string };
  initialUserDto?: UserDto;

  yearsList = generateYearsToDisplay();
  monthsList = generateMonthsToDisplay();
  daysList = generateDaysForMonthToDisplay(this.yearsList[0].value, this.monthsList[0].value);

  selectedYear?: number;
  selectedMonth?: number;
  selectedDay?: number;

  private selectedFile?: File;
  public avatar?: string;
  private initStateHash = '';

  constructor(
    private accountService: AccountService,
    private translate: TranslateService,
    private apiHttpService: ApiHttpService,
    private dataStorageService: DataStorageService,
    private router: Router,
    private dialogService: DialogService,
    private imageCropperService: ImageCropperService,
  ) {
    this.user = this.dataStorageService.user;
    this.setInitialUserDto(this.user);
    this.cognitoUser$ = accountService.user$;
    this.cognitoUser$.pipe(filter(user => !!user), take(1)).subscribe(user => this.setInitialCognitoUserData(user));
  }

  async ngOnInit() {
    this.avatar = (await this.apiHttpService.getUrlForProfilePicture()).Location;

    this.initStateHash = this.hashAccountFields();
  }

  navigateBack() {
    this.router.navigate(['calingo', 'account', 'view']);
  }

  hashAccountFields(): string {
    const user = this.accountService.user;

    if (!user) {
      return '';
    }

    const dataToHash = {
      avatarImgFilename: this.user?.avatarImgFilename,
      email: user.email,
      phoneNumber: this.user?.phoneNumber,
      city: this.user?.city,
      country: this.user?.country,
      street: this.user?.street,
      houseNumber: this.user?.houseNumber,
      zip: this.user?.zip,
      parent_birthdate: this.user?.parent_birthdate,
      firstName: this.user?.firstName,
      lastName: this.user?.lastName,
      selectedFile: this.selectedFile?.name,
    };

    return hashFields(dataToHash)
  }

  isFormChanged(): boolean {
    return this.initStateHash !== this.hashAccountFields();
  }

  /** Country name for the read-only field. Unknown codes are shown as they are. */
  get countryLabel(): string {
    const key = countryNameKey(this.user?.country);

    return key ? this.translate.instant(key) : (this.user?.country ?? '');
  }

  setInitialUserDto(user?: UserDto) {
    if (!user) {
      return;
    }

    const contract = this.dataStorageService.getAllData[0];

    if (!user.firstName) {
      user.firstName = contract.firstname;
    }

    if (!user.lastName) {
      user.lastName = contract.lastname;
    }

    if (!user.phoneNumber) {
      user.phoneNumber = contract.phone_number;
    }

    if (!user.city) {
      user.city = contract.city;
    }

    // CloudInsurance stores an ISO 3166-1 alpha-2 code, so this is `CH` and not
    // a country name. The field is read-only; the code is only ever displayed.
    if (!user.country) {
      user.country = contract.country || DEFAULT_COUNTRY_CODE;
    }

    if (!user.street) {
      user.street = contract.street;
    }

    if (!user.houseNumber) {
      user.houseNumber = contract.house_number;
    }

    if (!user.zip) {
      user.zip = contract.zip;
    }

    if (!user.parent_birthdate) {
      user.parent_birthdate = contract.parent_birthdate;
    }

    // Read the date parts straight off the string; new Date('YYYY-MM-DD') parses
    // as UTC midnight and getDate() would move the day behind negative offsets.
    const birthdateParts = user.parent_birthdate?.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);

    if (birthdateParts) {
      this.selectedYear = +birthdateParts[1];
      this.selectedMonth = +birthdateParts[2];
      this.selectedDay = +birthdateParts[3];

      this.setDaysList();
    }

    this.initialUserDto = { ...user };
  }

  setInitialCognitoUserData(user: CognitoAuthenticatedUser | null) {
    if (!user) {
      return;
    }
    this.initialCognitoUserData = { ...user };
    this.initialCognitoUserData.avatarImgFilename = this.dataStorageService.user?.avatarImgFilename;

    this.initStateHash = this.hashAccountFields();
  }

  // Cognito keeps the original email active until the code is confirmed, so on
  // cancel/failure restore the on-screen value to match what is actually stored.
  private revertCognitoEmail(cognitoAuthenticatedUser: CognitoAuthenticatedUser) {
    if (this.initialCognitoUserData) {
      cognitoAuthenticatedUser.email = this.initialCognitoUserData.email;
    }
  }

  async saveCognitoChanges(cognitoAuthenticatedUser: CognitoAuthenticatedUser) {
    let cognitoAttributesToUpdate: Partial<{ email: string, firstName: string, lastName: string }> = {}

    // Compare the typed value first: an old mixed-case email that the user did not
    // touch must not start an email change with a confirmation code.
    if (cognitoAuthenticatedUser.email != this.initialCognitoUserData?.email) {
      const email = normalizeEmail(cognitoAuthenticatedUser.email);
      cognitoAuthenticatedUser.email = email;

      if (email != this.initialCognitoUserData?.email) {
        cognitoAttributesToUpdate.email = email;
      }
    }

    const attributes = await this.accountService.handleUpdateEmailAndNameAttributes(cognitoAttributesToUpdate);

    const emailConfirmation = attributes?.email?.nextStep.updateAttributeStep === "CONFIRM_ATTRIBUTE_WITH_CODE";

    if (emailConfirmation) {
      this.translate.get(['account.modals.confirm-email.title', 'account.modals.confirm-email.message']).subscribe(translations => {
        const dialogRef = this.dialogService.openDialog(AccountAttributeChangeModalComponent, {
          data: {
            title: translations['account.modals.confirm-email.title'],
            message: translations['account.modals.confirm-email.message'],
            userAttributeKey: 'email',
          }
        });

        dialogRef.afterClosed().subscribe(async (result: GeneralConfirmationModalResult | string) => {
          if (result === GeneralConfirmationModalResult.Cancel) {
            this.revertCognitoEmail(cognitoAuthenticatedUser);
            return this.showFailModal();
          }

          const code = (result as string).trim();

          if (!code || code.length < 4 || /\D/.test(code)) {
            this.revertCognitoEmail(cognitoAuthenticatedUser);
            return this.showFailModal();
          }

          const response = await this.accountService.handleConfirmUserAttribute({
            confirmationCode: code,
            userAttributeKey: 'email'
          });

          if (response === 'error') {
            console.log(response);
            this.revertCognitoEmail(cognitoAuthenticatedUser);
            return this.showFailModal();
          } else {
            if (cognitoAttributesToUpdate.email && this.dataStorageService.user) {
              this.dataStorageService.user = {
                ...this.dataStorageService.user,
                email: cognitoAttributesToUpdate.email
              };
            }
            this.saveUserDataChanges(!!cognitoAttributesToUpdate.email);
            this.setInitialCognitoUserData(cognitoAuthenticatedUser);
            // this.showSuccessModal();
          }
        });
      })
    } else {
      if (cognitoAttributesToUpdate.email && this.dataStorageService.user) {
        this.dataStorageService.user = { ...this.dataStorageService.user, email: cognitoAttributesToUpdate.email };
      }
      this.saveUserDataChanges(!!cognitoAttributesToUpdate.email);
      // this.showSuccessModal();
      this.setInitialCognitoUserData(cognitoAuthenticatedUser);
    }
  }

  /**
   * @param emailChanged The Cognito email changed. The email is not in the
   * body, but `/save-user` must still run so the backend writes the new email
   * from the token to CloudInsurance.
   */
  async saveUserDataChanges(emailChanged = false) {
    const userDto = this.user;
    if (!userDto || !this.initialUserDto) {
      return;
    }

    // `email` is left out on purpose. The backend reads it from the Cognito
    // token, and the endpoint rejects it in the body.
    let userAttributesToSave: Partial<{
      avatarImgFilename: string;
      phoneNumber: string;
      city: string;
      country: string;
      street: string;
      houseNumber: string;
      zip: string;
      parent_birthdate: string;
      firstName: string;
      lastName: string;
    }> = {};

    if (this.selectedFile) {
      userAttributesToSave.avatarImgFilename = (await this.uploadFile())?.filename;
      this.selectedFile = undefined;
    }

    if (userDto.phoneNumber !== this.initialUserDto.phoneNumber) {
      userAttributesToSave.phoneNumber = userDto.phoneNumber;
    }

    if (userDto.city !== this.initialUserDto.city) {
      userAttributesToSave.city = userDto.city;
    }

    if (userDto.country !== this.initialUserDto.country) {
      userAttributesToSave.country = userDto.country;
    }

    if (userDto.street !== this.initialUserDto.street) {
      userAttributesToSave.street = userDto.street;
    }

    if (userDto.houseNumber !== this.initialUserDto.houseNumber) {
      userAttributesToSave.houseNumber = userDto.houseNumber;
    }

    if (userDto.zip !== this.initialUserDto.zip) {
      userAttributesToSave.zip = userDto.zip;
    }

    if (userDto.parent_birthdate !== this.initialUserDto.parent_birthdate) {
      userAttributesToSave.parent_birthdate = userDto.parent_birthdate;
    }

    if (userDto.firstName !== this.initialUserDto.firstName) {
      userAttributesToSave.firstName = userDto.firstName;
    }

    if (userDto.lastName !== this.initialUserDto.lastName) {
      userAttributesToSave.lastName = userDto.lastName;
    }

    //Check if userAttributesToSave not empty and we need to call api to save
    if (Object.keys(userAttributesToSave).length === 0 && !emailChanged) {
      return this.showSuccessModal();
    }

    const { avatarImgFilename, ...dataFields } = { ...userAttributesToSave };
    const onlyPicture = !!avatarImgFilename && Object.keys(dataFields).length === 0;

    // `/save-user` mails customer service itself, so `/update-user-mfv` is not
    // called any more. Both calls together produced two emails per change.
    let saved: UserDto | undefined;

    try {
      saved = await this.apiHttpService.saveUser(userAttributesToSave);
    } catch (error) {
      console.error(error);

      // A 502 means the insurance system did not take the data. Nothing was
      // saved, so the form keeps what the user typed and no reload is made.
      if (error instanceof ApiRequestError && error.isUpstreamFailure) {
        return this.showUpstreamFailModal();
      }

      return this.showFailModal();
    }

    await this.reloadUserFromApi(userAttributesToSave);

    // The endpoint names the fields it passed to customer service. They keep
    // their old value on screen, so the user is told what happens next.
    this.showSuccessModal(onlyPicture, saved?.pendingSupportFields ?? []);
  }

  // Once something was saved the backend is the source of truth, so pull the
  // fresh account data instead of keeping the locally merged copy.
  private async reloadUserFromApi(savedAttributes: Partial<UserDto>) {
    let freshUser: UserDto | undefined;

    try {
      freshUser = await this.apiHttpService.getCurrentUser();
    } catch (error) {
      // The save itself succeeded, so a failed reload is not shown as an error.
      console.error('Could not reload the profile after the save', error);
    }

    if (freshUser) {
      this.user = freshUser;
    } else if (this.user) {
      // Fall back to what was just sent when the reload call fails.
      this.user = { ...this.user, ...savedAttributes };
    }

    this.dataStorageService.user = this.user;
    this.setInitialUserDto(this.user);
    this.initStateHash = this.hashAccountFields();
  }

  /** Tells the user that the insurance system did not take the change. */
  showUpstreamFailModal() {
    this.translate.get(['account.modals.upstream-fail.title', 'account.modals.upstream-fail.message']).subscribe(translations => {
      this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['account.modals.upstream-fail.title'],
          message: translations['account.modals.upstream-fail.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          confirmButtonText: 'OK'
        } as GeneralConfirmationModalData
      });
    });
  }

  showFailModal() {
    this.translate.get(['account.modals.fail.title', 'account.modals.fail.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['account.modals.fail.title'],
          message: translations['account.modals.fail.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {

      });
    });
  }

  showSuccessModal(onlyPicture = false, supportFields: string[] = []) {
    // The response omits `pendingSupportFields` when nothing went to customer
    // service. CloudInsurance took the change, so it is on screen at once and
    // the user must not be told to wait 1-2 business days.
    const messageKey = supportFields.length > 0
      ? 'account.modals.success.message'
      : 'account.modals.success.message-applied';

    const keys = onlyPicture
      ? ['account.modals.success-picture.title', 'account.modals.success-picture.message']
      : ['account.modals.success.title', messageKey];
    this.translate.get(keys).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations[keys[0]],
          message: translations[keys[1]] + this.supportFieldsNote(supportFields),
          confirmButton: GeneralConfirmationModalResult.Confirm,
          confirmButtonText: 'OK'
        } as GeneralConfirmationModalData,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (this.backToProfile.observed) {
          this.backToProfile.emit();
        }
      });
    });
  }

  /**
   * Extra sentence for the success message when a change went to customer
   * service. The field keeps its old value on screen until a person applies it.
   */
  private supportFieldsNote(supportFields: string[]): string {
    const names = supportFields
      .filter(field => !!SUPPORT_FIELD_LABEL_KEYS[field])
      .map(field => this.translate.instant(SUPPORT_FIELD_LABEL_KEYS[field]))
      .join(', ');

    if (!names) {
      return '';
    }

    return '\n\n' + this.translate.instant('account.modals.success.support-note', { fields: names });
  }

  canDeactivate(): boolean | Observable<boolean> {
    const translations = this.translate.instant(['account.modals.leave-unsaved.title', 'account.modals.leave-unsaved.message']);
    return !this.isFormChanged() || this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['account.modals.leave-unsaved.title'],
        message: translations['account.modals.leave-unsaved.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    }).afterClosed().pipe(map((result: GeneralConfirmationModalResult) => {
      return result === GeneralConfirmationModalResult.Confirm;
    }));
  }

  async onFileChange($event: Event) {
    const input = $event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';

    const croppedFile = await this.imageCropperService.open(file);
    if (!croppedFile) return;

    this.selectedFile = croppedFile;
    this.setAvatarPreview(URL.createObjectURL(croppedFile));
  }

  private setAvatarPreview(url?: string) {
    if (this.avatar?.startsWith('blob:')) {
      URL.revokeObjectURL(this.avatar);
    }
    this.avatar = url;
  }

  ngOnDestroy() {
    this.setAvatarPreview(undefined);
  }

  async uploadFile() {
    if (!this.selectedFile) {
      return;
    }

    const s3SignedUrl = await this.getS3SignedUrl();

    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      await fetch(s3SignedUrl.location, {
        method: 'PUT',
        body: this.selectedFile,
        headers: {
          'Content-Type': this.selectedFile.type,
        }
      });

      return s3SignedUrl;
    } catch (error) {
      console.error('Error uploading file', error);
    }
    return;
  }

  async getS3SignedUrl() {
    return await this.apiHttpService.getSignedUrlToUploadFile();
  }

  setUserBirthday() {
    if (this.user) {
      if (this.selectedYear && this.selectedMonth && this.selectedDay) {
        const month = this.selectedMonth.toString().padStart(2, '0');
        const day = this.selectedDay.toString().padStart(2, '0');
        this.user.parent_birthdate = `${this.selectedYear}-${month}-${day}`;
      } else {
        this.user.parent_birthdate = undefined;
      }
    }
  }

  setDaysList() {
    if (this.selectedYear && this.selectedMonth) {
      this.daysList = generateDaysForMonthToDisplay(this.selectedYear, this.selectedMonth);

      if (this.selectedDay && this.daysList.findIndex(day => day.value === this.selectedDay) === -1) {
        this.selectedDay = undefined;
      }
    }
  }

  onYearChange() {
    // Rebuild the day list first, it can drop an out-of-range day (e.g. Feb 30)
    // that would otherwise be written into parent_birthdate.
    this.setDaysList();

    this.setUserBirthday();
  }

  onMonthChange() {
    this.setDaysList();

    this.setUserBirthday();
  }

  onDayChange() {
    this.setUserBirthday();
  }

  saveCognitoChangesPopup(cognitoAuthenticatedUser: CognitoAuthenticatedUser) {
    this.translate.get(['account.modals.save-changes.title', 'account.modals.save-changes.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['account.modals.save-changes.title'],
          message: translations['account.modals.save-changes.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
        } as GeneralConfirmationModalData,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          this.saveCognitoChanges(cognitoAuthenticatedUser);
        }
      });
    });
  }

}
